import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { ExternalLinkIcon, MessageSquareIcon } from 'lucide-react';
import {
  Button,
  CardHeader,
  EmptyState,
  ErrorNotice,
  FactList,
  LoadingState,
  PageHeading,
  Panel,
  useReturnTo,
} from '@bcecloud/portal-ui';
import { errorMessage } from '@/lib/api';
import { periodLabel, whenLabel } from '@/lib/report-window';
import { STATS_INTERVAL, readLead, salesforceLeadUrl, type Lead } from '@/lib/leads';
import { LeadCounts, LeadStatus, leadChannel } from './leads-list-section';

/** Where Back goes when the page was opened from a shared link. */
const LEADS_PATH = '/leads';

/** A Salesforce date-only value (`YYYY-MM-DD…`) as a readable day. */
function dayLabel(iso: string | null): string {
  return iso ? periodLabel(iso.slice(0, 10), STATS_INTERVAL.day) : '';
}

/** City, state and country, as far as the lead gave them. */
function placeLabel(lead: Lead): string {
  return [lead.city, lead.state, lead.country].filter(Boolean).join(', ');
}

/** A page path as a link to the live site, or nothing when the lead has none. */
function pageLink(path: string): React.ReactNode {
  if (!path) return '';
  const href = /^https?:\/\//.test(path) ? path : `https://www.bestcorporateevents.com${path}`;
  return (
    <a href={href} target="_blank" rel="noreferrer" className="break-all text-brand hover:underline">
      {path}
    </a>
  );
}

/** A record link into Salesforce, labelled in the reader's words. */
function salesforceLink(salesforceId: string | null, label: string): React.ReactNode {
  if (!salesforceId) return '';
  return (
    <a
      href={salesforceLeadUrl(salesforceId)}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center gap-1 text-brand hover:underline"
    >
      {label} <ExternalLinkIcon className="size-3.5" aria-hidden="true" />
    </a>
  );
}

/**
 * One lead from the Salesforce mirror on its own page: who they are, what
 * they wrote, their event, where it stands and how they found us. Everything
 * here is the synced copy, so reading a lead never needs Salesforce; the
 * "View in Salesforce" button is there for editing it.
 *
 * @returns The lead detail screen.
 */
export function LeadDetailPage(): React.ReactElement {
  const { leadId } = useParams<{ leadId: string }>();
  const backTo = useReturnTo(LEADS_PATH);
  const lead = useQuery({
    queryKey: ['leads', 'detail', leadId],
    queryFn: ({ signal }) => readLead(leadId ?? '', signal),
    enabled: leadId !== undefined,
  });

  if (lead.isPending) return <LoadingState label="Loading lead…" />;
  if (lead.isError) {
    return (
      <section className="animate-portal-rise">
        <PageHeading backTo={backTo} breadcrumbs={[{ label: 'Analytics' }, { label: 'Leads', to: backTo }]} title="Lead" />
        <ErrorNotice title="This lead is unavailable" message={errorMessage(lead.error)} />
      </section>
    );
  }

  const detail = lead.data;
  const title = detail.name || detail.company || 'Unnamed lead';
  const subtitle = [detail.name ? detail.company : '', `Arrived ${whenLabel(detail.sfCreatedAt)}`]
    .filter(Boolean)
    .join(' · ');
  const message = detail.description.trim();

  return (
    <section className="animate-portal-rise">
      <PageHeading
        backTo={backTo}
        breadcrumbs={[{ label: 'Analytics' }, { label: 'Leads', to: backTo }]}
        title={title}
        subtitle={subtitle}
        action={
          <Button variant="secondary" href={salesforceLeadUrl(detail.salesforceId)} target="_blank" rel="noreferrer">
            <ExternalLinkIcon className="size-4" aria-hidden="true" /> View in Salesforce
          </Button>
        }
      />

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="grid content-start gap-4 lg:col-span-2">
          <Panel className="p-5">
            <CardHeader title="What they wrote" description="Their message from the form, or the notes on the lead." />
            {message ? (
              <p className="mt-4 whitespace-pre-wrap text-portal-body text-portal-ink">{message}</p>
            ) : (
              <EmptyState
                icon={<MessageSquareIcon className="size-5" aria-hidden="true" />}
                title="No message"
                message="This lead came in without a message or notes."
              />
            )}
          </Panel>

          <Panel className="p-5">
            <CardHeader title="Their event" description="What they asked for, as far as the form said." />
            <div className="mt-4">
              <FactList
                columns={3}
                showEmpty
                facts={[
                  { label: 'Event city', value: detail.eventCity },
                  { label: 'Event date', value: dayLabel(detail.eventDate) },
                  {
                    label: 'Group size',
                    value: detail.participantCount !== null ? `${detail.participantCount} people` : '',
                  },
                  { label: 'Programs', value: detail.programSelections.join(', ') },
                ]}
              />
            </div>
          </Panel>
        </div>

        <div className="grid content-start gap-4">
          <Panel className="p-5">
            <CardHeader title="Contact" description="How to reach them." />
            <div className="mt-4">
              <FactList
                columns={2}
                showEmpty
                facts={[
                  {
                    label: 'Email',
                    value: detail.email ? (
                      <a href={`mailto:${detail.email}`} className="break-all text-brand hover:underline">
                        {detail.email}
                      </a>
                    ) : (
                      ''
                    ),
                  },
                  {
                    label: 'Phone',
                    value: detail.phone ? (
                      <a href={`tel:${detail.phone}`} className="text-brand hover:underline">
                        {detail.phone}
                      </a>
                    ) : (
                      ''
                    ),
                  },
                  { label: 'Company', value: detail.company },
                  { label: 'Title', value: detail.title },
                  { label: 'Location', value: placeLabel(detail) },
                ]}
              />
            </div>
          </Panel>

          <Panel className="p-5">
            <CardHeader title="Where it stands" description="Who owns it and what became of it." />
            <div className="mt-4">
              <FactList
                columns={2}
                showEmpty
                facts={[
                  { label: 'What happened', value: <LeadStatus lead={detail} /> },
                  { label: 'Counts as', value: <LeadCounts lead={detail} /> },
                  { label: 'Lead status', value: detail.status },
                  { label: 'Owner', value: detail.ownerName },
                  { label: 'Salesperson', value: detail.salesperson },
                  { label: 'Converted', value: detail.isConverted ? dayLabel(detail.convertedAt) || 'Yes' : 'Not yet' },
                  { label: 'Opportunity', value: salesforceLink(detail.convertedOpportunityId, detail.booking?.stageName || 'Open') },
                ]}
              />
            </div>
          </Panel>
        </div>
      </div>

      <Panel className="mt-4 p-5">
        <CardHeader title="How they found us" description="The channel, the pages they came through and the campaign that sent them." />
        <div className="mt-4">
          <FactList
            columns={4}
            phoneColumns={2}
            facts={[
              { label: 'Channel', value: leadChannel(detail) },
              { label: 'They said', value: detail.howDidYouHear },
              { label: 'Created by', value: detail.createdByName },
              { label: 'Google Ads click', value: detail.attribution.gclid ? 'Yes' : '' },
              { label: 'Landing page', value: pageLink(detail.attribution.landingPage) },
              { label: 'Form page', value: pageLink(detail.attribution.convertPage) },
              { label: 'Referring page', value: pageLink(detail.attribution.referringPage) },
              { label: 'Campaign', value: detail.attribution.utmCampaign },
              { label: 'Source / medium', value: [detail.attribution.utmSource, detail.attribution.utmMedium].filter(Boolean).join(' / ') },
              { label: 'Search term', value: detail.attribution.utmTerm },
            ]}
          />
        </div>
      </Panel>
    </section>
  );
}
